// =============================================================================
// features/sales/campaigns/operate/useCampaignSummary.ts — Leitura mínima de
// UMA campanha (nome + status) para o cabeçalho e o gate de "campanha
// operável" desta tela (`GET /api/sales/campaigns/:id`, F35-S02, doc 26 §9).
//
// Deliberadamente FORA de hooks/sales/useCampaigns.ts (não está em
// `files_allowed` deste slot — ver frontmatter de F35-S10): esta é uma
// leitura pontual por id, key própria (`['sales','campaigns','detail', id]`),
// não colide com `campaignKeys.list(...)` de useCampaigns.ts.
// =============================================================================

import type { Campaign } from '@elemento/shared-schemas';
import { CampaignSchema } from '@elemento/shared-schemas';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../../../lib/api';

export const campaignSummaryKey = (campaignId: string) =>
  ['sales', 'campaigns', 'detail', campaignId] as const;

async function fetchCampaign(campaignId: string): Promise<Campaign> {
  const raw = await api.get<unknown>(`/api/sales/campaigns/${encodeURIComponent(campaignId)}`);
  return CampaignSchema.parse(raw);
}

export function useCampaignSummary(campaignId: string): {
  campaign: Campaign | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const enabled = campaignId.length > 0;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: campaignSummaryKey(campaignId),
    queryFn: () => fetchCampaign(campaignId),
    enabled,
    staleTime: 15_000,
  });

  return {
    campaign: data,
    isLoading: enabled && isLoading,
    isError,
    refetch: () => void refetch(),
  };
}
